/*
 * Copyright (c) 2024, King Living
 * Cart Flyout Theme Styles
 */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';
import {A11Y_ICON_GLYPH_SIZE, A11Y_ICON_TARGET_MIN} from '@amblique/king-living/app/theme/foundations/a11y-targets';

export const cartFlyoutLinenBackground =
  'linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%), #F7F5F0';
const CartFlyout = {
  parts: [
    'overlay',
    'headerBackground',
    'content',
    'header',
    'headerCloseBar',
    'headerTitleRow',
    'headerMobileTop',
    'headerMobileTitleRow',
    'closeButton',
    'logo',
    'logoSvg',
    'title',
    'itemCount',
    'body',
    'itemsContainer',
    'lineItem',
    'itemDivider',
    'deleteButton',
    'swipeableItemTrackDivider',
    'loadingState',
    'emptyState',
    'emptyStateContent',
    'emptyStateIcon',
    'emptyStateTitle',
    'emptyStateMessage',
    'emptyStateTextBlock',
    'emptyStateActions',
    'emptyStateSignInBtn',
    'footer',
    'pricingSummary',
    'pricingRows',
    'pricingDivider',
    'totalBlock',
    'priceRow',
    'priceRowLabel',
    'priceRowValue',
    'checkoutButton',
    'paymentIcons',
    'paymentIcon',
    'itemRoot',
    'itemThumbnail',
    'itemContent',
    'itemName',
    'itemShortDesc',
    'itemConfigGroup',
    'itemConfigText',
    'itemPromoText',
    'itemActions',
    'itemCta',
    'itemCtaDisabled',
    'itemRemoveCta',
    'itemPriceCol',
    'itemCurrentPrice',
    'itemOriginalPrice',
    'itemLoadingOverlay',
    'itemQuantity',
    'qtyControl',
    'qtyMaxWarning',
    'qtyMaxError',
    'qtyMaxErrorText',
    'itemCtaGroup',
    'itemCtaSeparator',
    'itemOutOfStockAlert',
    'itemOutOfStockAlertText',
  ],
  baseStyle: {
    overlay: {
      backdropFilter: 'blur(8px)',
      bg: 'blackAlpha.300',
    },
    // Flex column between content + footer so DrawerBody (`flex: 1`, `overflowY: auto`) can scroll.
    // Fixed 100dvh here broke scroll after wrapping header+body (body flex only works on a flex parent).
    headerBackground: {
      background: {base: 'whiteAlpha.700', lg: cartFlyoutLinenBackground},
      display: 'flex',
      flex: '1 1 0%',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
    },
    content: {
      background: {base: 'brandColors.primaryBG', lg: 'transparent'},
      display: 'flex',
      flexDirection: 'column',
      h: {base: '100dvh', lg: '100%'},
      maxH: {base: '100dvh', lg: '100%'},
      maxWidth: {base: '100%', lg: '480px'},
      minH: {base: 0, lg: 'auto'},
      overflow: 'hidden',
      position: 'relative',
    },
    header: {
      background: 'transparent',
      borderBottom: 'none',
      boxShadow: 'none',
      flexShrink: 0,
      margin: 0,
    },
    headerCloseBar: {
      alignItems: 'flex-start',
      display: {base: 'none', lg: 'flex'},
      flexShrink: 0,
      justifyContent: 'flex-end',
      paddingBottom: '24px',
      paddingTop: '24px',
      width: '100%',
    },
    headerTitleRow: {
      alignItems: 'flex-end',
      display: {base: 'none', lg: 'flex'},
      justifyContent: 'space-between',
      paddingBottom: '32px',
      width: '100%',
    },
    headerMobileTop: {
      alignItems: 'center',
      borderBottom: '1px solid',
      borderColor: 'greyscale90Alpha.10',
      display: {base: 'flex', lg: 'none'},
      justifyContent: 'space-between',
      marginX: {base: '-20px', lg: 0},
      paddingY: '20px',
      paddingLeft: {base: '9px', lg: 0},
      paddingRight: {base: '12px', lg: 0},
      width: {base: 'calc(100% + 40px)', lg: '100%'},
      backgroundColor: 'brandColors.primaryBG',
      height: '61px',
    },
    headerMobileTitleRow: {
      alignItems: 'center',
      display: {base: 'flex', lg: 'none'},
      gap: '12px',
      justifyContent: 'space-between',
      paddingBottom: '24px',
      paddingTop: '32px',
      width: '100%',
    },
    closeButton: {
      _active: {bg: 'transparent'},
      _hover: {bg: 'transparent'},
      ...A11Y_FOCUS_VISIBLE,
      alignItems: 'center',
      appearance: 'none',
      bg: 'transparent',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      color: 'greyscale.100',
      cursor: 'pointer',
      display: 'inline-flex',
      flexShrink: 0,
      h: A11Y_ICON_TARGET_MIN,
      justifyContent: 'center',
      lineHeight: 0,
      minH: A11Y_ICON_TARGET_MIN,
      minW: A11Y_ICON_TARGET_MIN,
      outline: 'none',
      p: 0,
      paddingRight: {base: '20px', lg: 0},
      w: A11Y_ICON_TARGET_MIN,
      sx: {
        svg: {
          height: A11Y_ICON_GLYPH_SIZE,
          width: A11Y_ICON_GLYPH_SIZE,
        },
      },
    },
    logo: {
      display: {base: 'block', lg: 'none'},
      height: '17px',
      paddingLeft: {base: '20px', lg: 0},
      width: '76px',
    },
    logoSvg: {
      '& svg': {
        display: 'block',
        height: '17px',
        width: '76px',
      },
    },
    title: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: {base: '16px', lg: '22px'},
      fontWeight: 'semibold',
      letterSpacing: {base: 0, lg: '-0.22px'},
      lineHeight: 1.2,
      margin: 0,
    },
    itemCount: {
      color: 'greyscale90Alpha.70',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 'normal',
      lineHeight: 1.2,
      margin: 0,
      textAlign: {base: 'right', lg: 'right'},
    },
    body: {
      bg: 'transparent',
      flex: '1 1 0%',
      minHeight: 0,
      overflowY: 'auto',
      padding: 0,
      WebkitOverflowScrolling: 'touch',
    },
    itemsContainer: {
      paddingX: {base: '20px', lg: '24px'},
      width: '100%',
    },
    lineItem: {
      touchAction: 'pan-y',
    },
    itemDivider: {
      bg: 'greyscale90Alpha.10',
      flexShrink: 0,
      height: '1px',
      marginTop: '24px',
      width: '100%',
    },
    swipeableItemTrackDivider: {
      borderBottom: '1px solid',
      borderColor: 'greyscale90Alpha.10',
    },
    deleteButton: {
      alignItems: 'center',
      bg: 'greyscale.100',
      color: 'white',
      display: 'flex',
      flexShrink: 0,
      fontFamily: 'body',
      fontSize: '16px',
      fontWeight: 'normal',
      justifyContent: 'center',
      lineHeight: 1.2,
      padding: '10px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      width: '80px',
    },
    loadingState: {
      paddingX: 0,
      paddingY: 6,
      textAlign: 'center',
    },
    emptyState: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      minHeight: {base: '280px', lg: '360px'},
      paddingY: {base: 10, lg: 12},
      width: '100%',
    },
    emptyStateContent: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      maxWidth: '100%',
      width: '100%',
    },
    emptyStateIcon: {
      alignItems: 'center',
      color: 'brandColors.greyscale90',
      display: 'flex',
      flexShrink: 0,
      height: {base: '40px', lg: '48px'},
      justifyContent: 'center',
      width: {base: '40px', lg: '48px'},
    },
    emptyStateTitle: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontSize: {base: '16px', lg: '18px'},
      fontWeight: 'semibold',
      lineHeight: 1.2,
      margin: 0,
      textAlign: 'center',
    },
    emptyStateMessage: {
      color: 'greyscale90Alpha.70',
      fontFamily: 'body',
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: 1.2,
      margin: 0,
      textAlign: 'center',
    },
    emptyStateTextBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
    },
    emptyStateActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%',
      height: '54px',
      sx: {
        '& a': {
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '600',
          height: '54px',
          lineHeight: '140%',
          maxHeight: '54px',
          minHeight: '54px',
          minWidth: '100%',
          padding: '16px 48px',
          width: '100%',
        },
        '& a:hover': {
          textDecoration: 'none',
        },
        '& a:focus, & a:focus-visible, & a[data-focus-visible]': {
          boxShadow: 'none',
          outline: '2px solid',
          outlineColor: 'blue.500',
          outlineOffset: '2px',
        },
      },
    },
    emptyStateSignInBtn: {
      gap: '0',
      height: '54px',
      sx: {
        '& a': {
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '600',
          height: '54px',
          lineHeight: '140%',
          maxHeight: '54px',
          minHeight: '54px',
          minWidth: '100%',
          padding: '16px 48px',
          width: '100%',
        },
        '& .chakra-button__icon': {
          height: '18px',
          width: '18px',
          '& svg': {
            height: '18px',
            width: '18px',
          },
        },
        '&&:hover': {
          textDecoration: 'none',
        },
      },
    },
    footer: {
      background: 'brandColors.primaryBG',
      borderTop: 'none',
      flexShrink: 0,
      margin: 0,
      padding: 0,
      paddingBottom: {base: 'env(safe-area-inset-bottom, 0px)', lg: 0},
    },
    pricingSummary: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      paddingX: {base: '20px', lg: '24px'},
      paddingY: '24px',
      width: '100%',
    },
    pricingRows: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      width: '100%',
    },
    pricingDivider: {
      bg: 'greyscale90Alpha.10',
      flexShrink: 0,
      height: '1px',
      width: '100%',
    },
    totalBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      width: '100%',
    },
    priceRow: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    priceRowLabel: {
      color: 'greyscale90Alpha.70',
      flex: 1,
      fontFamily: 'body',
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: 1.2,
      minWidth: 0,
    },
    priceRowValue: {
      color: 'greyscale90Alpha.70',
      flex: 1,
      fontFamily: 'body',
      fontSize: '14px',
      fontWeight: 'normal',
      lineHeight: 1.2,
      minWidth: 0,
      textAlign: 'right',
    },
    checkoutButton: {
      _active: {bg: 'greyscale.90'},
      _focus: {boxShadow: 'none', outline: 'none'},
      _focusVisible: {boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)'},
      _hover: {
        bg: 'white',
        borderColor: 'greyscale.100',
        color: 'greyscale.100',
      },
      alignItems: 'center',
      appearance: 'none',
      bg: 'greyscale.100',
      border: '1px solid',
      borderColor: 'greyscale.100',
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer',
      display: 'inline-flex',
      flexShrink: 0,
      fontFamily: 'body',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      gap: '8px',
      justifyContent: 'center',
      lineHeight: '140%',
      sx: {
        '&:hover svg': {
          color: 'greyscale.100',
        },
      },
      w: '100%',
    },
    paymentIcons: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: '8px',
      width: {base: '100%', lg: 'auto'},
    },
    paymentIcon: {
      aspectRatio: {base: '38 / 24', lg: 'auto'},
      border: {base: '1px solid', lg: 'none'},
      borderColor: {base: 'greyscale90Alpha.10', lg: 'transparent'},
      borderRadius: {base: '2px', lg: 0},
      flex: {base: '1 0 0', lg: '0 0 auto'},
      flexShrink: 0,
      minH: '32px',
      minW: {base: 0, lg: 'auto'},
      overflow: 'hidden',
      w: {base: 'auto', lg: '51px'},
      sx: {
        '& img': {
          height: '100%',
          objectFit: 'contain',
          width: '100%',
        },
        '& .chakra-text': {
          display: 'none',
        },
      },
    },
    itemRoot: {
      alignItems: 'flex-start',
      gap: {base: '24px', lg: '24px'},
      position: 'relative',
      width: '100%',
      sx: {
        // KL-1035 — Hide cart line-item quantity picker + Edit link (cart + checkout).
        '& .cart-item__qty-wrapper, & .cart-item__edit-btn, & .cart-item__cta-sep': {
          display: 'none'
        },
      },
    },
    itemRootFlyout: {
      flexWrap: 'nowrap',
    },
    itemThumbnail: {
      bg: 'brandColors.primaryLinen15',
      flexShrink: 0,
      height: '80px',
      overflow: 'hidden',
      width: '80px',
    },
    itemContent: {
      direction: 'column',
      flex: 1,
      gap: '8px',
      minWidth: 0,
      paddingTop: '8px',
    },
    itemName: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: '600',
      lineHeight: 1.4,
      sx: {
        '.cart-flyout__items-container &': {
          fontWeight: '500',
          fontSize: {base: '14px', lg: '14px'},
        },
      }
    },
    itemShortDesc: {
      color: 'greyscale90Alpha.70',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: '400',
      lineHeight: 1.2,
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '12px', lg: '12px'},
        },
      }
    },
    itemConfigGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      width: '100%',
      '& > *': {
        margin: 0,
      },
    },
    itemConfigText: {
      color: 'greyscale90Alpha.70',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: '400',
      lineHeight: 1.2,
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '12px', lg: '12px'},
        },
      },
    },
    itemPromoText: {
      color: 'secondary.newSaleRed',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: '400',
      lineHeight: 1.2,
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '12px', lg: '12px'},
          fontWeight: '500',
        },
      },
    },
    itemQuantity: {
      flexShrink: 0,
      maxWidth: '112px',
      width: '112px',
    },
    itemActions: {
      alignItems: {base: 'center', lg: 'center'},
      flexDirection: {base: 'row', lg: 'row'},
      flexWrap: {base: 'wrap', lg: 'nowrap'},
      gap: {base: '12px', lg: '24px'},
      marginTop: '24px',
      rowGap: {base: '12px', lg: '24px'},
      width: {base: '100%', lg: 'auto'},
      sx: {
        '.cart-flyout__items-container &': {
          marginTop: '12px',
        },
      }
    },
    itemCta: {
      color: 'primary.darkLinen',
      cursor: 'pointer',
      display: 'inline-block',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: '400',
      lineHeight: 1.4,
      position: 'relative',
      textDecoration: 'none',
      _after: {
        bg: 'currentColor',
        bottom: '-4px',
        content: '""',
        height: '1px',
        left: 0,
        position: 'absolute',
        right: 0,
      },
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '16px', lg: '14px'},
        },
      }
    },
    itemCtaDisabled: {
      color: 'greyscale.40',
      cursor: 'not-allowed',
      pointerEvents: 'none',
      _after: {
        bg: 'currentColor',
        bottom: '-3px',
        content: '""',
        height: '1px',
        left: 0,
        position: 'absolute',
        right: 0,
      },
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '16px', lg: '14px'},
        },
      }
    },
    itemRemoveCta: {
      color: 'primary.darkLinen',
      cursor: 'pointer',
      display: 'inline-block',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: '400',
      lineHeight: 1.4,
      position: 'relative',
      textDecoration: 'none',
      _after: {
        bg: 'currentColor',
        bottom: '-3px',
        content: '""',
        height: '1px',
        left: 0,
        position: 'absolute',
        right: 0,
      },
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '16px', lg: '14px'},
        },
      }
    },
    itemCtaGroup: {
      alignItems: 'center',
      gap: '24px',
      sx: {
        '.cart-flyout__items-container &': {
          gap: {base: '24px', lg: '12px'},
        },
      }
    },
    itemCtaSeparator: {
      alignSelf: 'stretch',
      bg: 'greyscale90Alpha.10',
      flexShrink: 0,
      width: '1px',
      height: '17px',
    },
    itemPriceCol: {
      alignItems: 'flex-end',
      direction: 'column',
      flexShrink: 0,
      gap: '6px',
      minWidth: '65px',
      paddingY: '8px',
      textAlign: 'right',
      width: 'auto',
      sx: {
        '.cart-flyout__items-container &': {
          minWidth: '45px',
        },
      }
    },
    itemCurrentPrice: {
      color: 'brandColors.greyscale90',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '14px', lg: '16px'},
      fontStyle: 'normal',
      fontWeight: '600',
      lineHeight: 1.4,
      textAlign: 'right',
      whiteSpace: 'nowrap',
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '14px', lg: '14px'},
          fontWeight: '500',
        },
      }
    },
    itemOriginalPrice: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 1.2,
      textAlign: 'right',
      textDecoration: 'line-through',
      whiteSpace: 'nowrap',
      sx: {
        '.cart-flyout__items-container &': {
          fontSize: {base: '12px', lg: '12px'},
        },
      }
    },
    itemLoadingOverlay: {
      alignItems: 'center',
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 2,
    },
    qtyControl: {},
    qtyMaxWarning: {
      color: 'secondary.newSaleRed',
      fontSize: 'xs',
      marginTop: 1,
    },
    qtyMaxError: {
      bg: 'error.background',
      border: '1px solid',
      borderColor: 'secondary.newSaleRed',
      borderRadius: '4px',
      color: 'secondary.newSaleRed',
      marginTop: 2,
      padding: '8px 12px',
      width: '100%',
    },
    qtyMaxErrorText: {
      color: 'secondary.newSaleRed',
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: 1.2,
    },
    itemOutOfStockAlert: {
      alignItems: 'center',
      display: 'flex',
      gap: 2,
      marginTop: 2,
      px: 4,
      py: 1,
      width: '100%',
    },
    itemOutOfStockAlertFlyout: {
      alignItems: 'center',
      display: 'flex',
      flexBasis: '100%',
      gap: 2,
      marginBottom: 4,
      marginTop: 4,
      px: 4,
      py: 2,
      width: '100%',
      sx: {
        '.cart-flyout__items-container &': {
          marginBottom: '0',
          marginTop: '0',
        },
      }
    },
    itemOutOfStockAlertText: {
      color: 'brandColors.greyscale90',
      flex: 1,
      fontSize: 'xs',
      lineHeight: 1.2,
      margin: 0,
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
};

export default CartFlyout;
